<script setup>
import { computed, onUnmounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';


// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);
const currentInbox = computed(() =>
  store.getters['inboxes/getInboxById'](currentChat.value.inbox_id)
);
const currentContact = computed(() =>
  store.getters['contacts/getContact'](currentChat.value.meta?.sender?.id)
);

const sender = computed(() => currentChat.value?.meta?.sender || {});
const contactPhoneNumber = computed(
  () => currentContact.value?.phone_number || sender.value?.phone_number || ''
);
const contactName = computed(
  () => currentContact.value?.name || sender.value?.name || ''
);
const isWhatsappWebInbox = computed(
  () => currentInbox.value?.channelType === INBOX_TYPES.WHATSAPP_WEB
);

const clickToCallUrl = computed(() => {
  const phoneNumber = contactPhoneNumber.value;
  if (!phoneNumber) {
    return '';
  }

  const wavoipToken = currentInbox.value?.wavoipToken;
  if (!isWhatsappWebInbox.value || !wavoipToken) {
    return '';
  }

  const normalizedPhoneNumber = phoneNumber.replace(/\D/g, '');
  if (!normalizedPhoneNumber) {
    return '';
  }

  const params = new URLSearchParams({
    token: wavoipToken,
    phone: normalizedPhoneNumber,
    start_if_ready: 'true',
    available_after_call: 'false',
    close_after_call: 'true',
  });
  if (contactName.value) {
    params.append('name', contactName.value);
  }

  return `https://app.wavoip.com/call?${params.toString()}`;
});

const showCallButton = computed(
  () => isWhatsappWebInbox.value && Boolean(contactPhoneNumber.value)
);

const openClickToCall = () => {
  if (!currentInbox.value?.wavoipToken) {
    useAlert(t('CONVERSATION.HEADER.CALL_ACTION_MISSING_TOKEN'));
    return;
  }
  if (!clickToCallUrl.value) {
    useAlert(t('CONVERSATION.HEADER.CALL_ACTION_MISSING_PHONE'));
    return;
  }
  window.open(
    clickToCallUrl.value,
    'wavoip',
    'width=300,height=500,toolbar=no,scrollbars=no,resizable=yes'
  );
};

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  // Schedule message option moved to ContactPanel.vue

  return items;
});

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ButtonV4
      v-if="showCallButton"
      v-tooltip="$t('CONVERSATION.HEADER.CALL_ACTION')"
      size="sm"
      variant="ghost"
      color="slate"
      icon="i-ri-phone-line"
      class="rounded-md hover:bg-n-alpha-2"
      @click="openClickToCall"
    />
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
</template>
